import React, { ReactNode } from 'react';
import { ButtonVariant, ButtonSize } from '../../types';

interface ButtonProps {
  variant?:   ButtonVariant;
  size?:      ButtonSize;
  loading?:   boolean;
  disabled?:  boolean;
  iconLeft?:  ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
  as?:        'button' | 'a';
  href?:      string;
  onClick?:   () => void;
  children:   ReactNode;
  className?: string;
  type?:      'button' | 'submit' | 'reset';
  target?:    string;
  rel?:       string;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-teal text-navy hover:bg-teal-light hover:shadow-[0_8px_24px_rgba(0,180,160,0.35)] hover:-translate-y-0.5',
  ghost:   'bg-transparent text-white border border-white/20 hover:border-teal hover:text-teal hover:bg-teal/[0.06]',
  dark:    'bg-navy text-white hover:bg-teal hover:text-navy hover:-translate-y-0.5',
  outline: 'bg-transparent text-teal border border-teal hover:bg-teal hover:text-navy hover:-translate-y-0.5',
  danger:  'bg-red-500 text-white hover:bg-red-600 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(239,68,68,0.3)]',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'text-[0.8rem] px-[1.1rem] py-[0.55rem]',
  md: 'text-[0.92rem] px-[1.8rem] py-[0.85rem]',
  lg: 'text-[1.02rem] px-[2.2rem] py-[1.05rem]',
};

export default function Button({
  variant   = 'primary',
  size      = 'md',
  loading   = false,
  disabled  = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  as        = 'button',
  href,
  onClick,
  children,
  className = '',
  type      = 'button',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;

  const base = [
    'inline-flex items-center justify-center gap-2 font-syne font-semibold tracking-wide',
    'rounded-md transition-all duration-200 outline-none whitespace-nowrap',
    'focus-visible:outline-2 focus-visible:outline-teal focus-visible:outline-offset-[3px]',
    VARIANTS[variant],
    SIZES[size],
    isDisabled ? 'opacity-45 cursor-not-allowed !transform-none !shadow-none' : '',
    loading    ? 'cursor-wait' : '',
    fullWidth  ? 'w-full' : '',
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {loading && (
        <span className="flex items-center flex-shrink-0">
          <svg className="animate-[btnSpin_0.75s_linear_infinite]" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeOpacity="0.25" />
            <path d="M14 8a6 6 0 00-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      )}
      {!loading && iconLeft  && <span className="flex items-center flex-shrink-0">{iconLeft}</span>}
      <span className={loading ? 'opacity-70' : ''}>{children}</span>
      {!loading && iconRight && <span className="flex items-center flex-shrink-0">{iconRight}</span>}
    </>
  );

  if (as === 'a') {
    return (
      <a href={isDisabled ? undefined : href} className={base}
        onClick={isDisabled ? undefined : onClick} aria-disabled={isDisabled} {...rest}>
        {content}
      </a>
    );
  }

  return (
    <button type={type} className={base} disabled={isDisabled}
      onClick={onClick} aria-busy={loading} {...rest}>
      {content}
    </button>
  );
}
